import React, { useEffect, useState } from 'react';
import { swilRequest } from '../services/swil';
import './BusinessTrend.css';

const CACHE_HOURS = 6; // keeps SWIL calls down: one invoice lookup per customer every few hours
const IST = 'Asia/Kolkata';

const pad = (n) => String(n).padStart(2, '0');
function todayIst() {
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', { timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).split('-').map(Number);
  return { y, m, d };
}
// The current month and the two before it, oldest first.
function lastThreeMonths() {
  const { y, m, d } = todayIst();
  const months = [-2, -1, 0].map((offset) => {
    const date = new Date(Date.UTC(y, m - 1 + offset, 1));
    return { key: `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`, label: date.toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' }), current: offset === 0 };
  });
  return { months, from: `${months[0].key}-01`, to: `${y}-${pad(m)}-${pad(d)}` };
}
const amountOf = (value) => Number(String(value ?? '').replace(/[^0-9.-]/g, '')) || 0;
function short(value) {
  try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', notation: 'compact', maximumFractionDigits: 1 }).format(value); }
  catch { return `₹${Math.round(value).toLocaleString('en-IN')}`; }
}

// Faint chart of what the customer was invoiced by S C Paul in the last three months (SWIL invoices).
export default function BusinessTrend({ customer }) {
  const customerId = customer?.customer_id;
  const [data, setData] = useState(null); // null = loading, false = unavailable
  useEffect(() => {
    if (!customerId) return undefined;
    const { months, from, to } = lastThreeMonths();
    const key = `cw_business_trend_${customerId}`;
    try {
      const cached = JSON.parse(localStorage.getItem(key));
      if (cached && cached.from === from && Date.now() - cached.at < CACHE_HOURS * 3600 * 1000) { setData(cached.months); return undefined; }
    } catch { /* no usable cache */ }
    const controller = new AbortController();
    swilRequest('invoices', { customer_id: customerId, from, to }, { signal: controller.signal })
      .then((result) => {
        const totals = Object.fromEntries(months.map((mo) => [mo.key, 0]));
        (result.rows || []).forEach((row) => { const k = String(row.date || '').slice(0, 7); if (k in totals) totals[k] += amountOf(row.amount); });
        const filled = months.map((mo) => ({ ...mo, value: Math.round(totals[mo.key] * 100) / 100 }));
        setData(filled);
        try { localStorage.setItem(key, JSON.stringify({ at: Date.now(), from, months: filled })); } catch { /* storage full: fine */ }
      })
      .catch(() => { if (!controller.signal.aborted) setData(false); });
    return () => controller.abort();
  }, [customerId]);

  if (!customerId || data === false) return null; // ERP unavailable: keep the welcome panel clean
  const max = data ? Math.max(...data.map((m) => m.value), 1) : 1;
  const total = data ? data.reduce((sum, m) => sum + m.value, 0) : 0;
  return <figure className="cw-trend" aria-label="Your business with S C Paul in the last 3 months">
    <figcaption>
      <span>LAST 3 MONTHS WITH S C PAUL</span>
      {data && <strong>{short(total)}</strong>}
    </figcaption>
    <div className="cw-trend-bars">
      {(data || [{ key: 'a' }, { key: 'b' }, { key: 'c' }]).map((m, i) => <div className={`cw-trend-col ${m.current ? 'is-current' : ''} ${data ? '' : 'is-loading'}`} key={m.key}>
        <span className="cw-trend-value">{data ? short(m.value) : ''}</span>
        <span className="cw-trend-track"><i style={{ height: data ? `${Math.max(4, (m.value / max) * 100)}%` : `${30 + i * 20}%` }} /></span>
        <span className="cw-trend-label">{data ? (m.current ? `${m.label} · so far` : m.label) : ''}</span>
      </div>)}
    </div>
    {data && !total && <p className="cw-trend-empty">No invoices in the last 3 months yet.</p>}
  </figure>;
}
